import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Car, GripVertical, Pencil, Plus, Share2, Trash2 } from 'lucide-react'
import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { EntityAccessPanel } from '../components/entity-access-panel'
import { useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Dialog } from '../components/ui/dialog'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { PageHeader } from '../components/ui/page-header'
import { SidePanel } from '../components/ui/side-panel'
import { TabNav } from '../components/ui/tab-nav'
import { api, type LogbookTrip, type LogbookTripInput, type LogbookVehicle } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logbook',
  component: LogbookPage,
})

// --- Helfer: Geld (Cent) + Liter (Dezimal) <-> Eingabe ---------------------
function centsToInput(c: number | null | undefined): string {
  return c == null ? '' : (c / 100).toFixed(2).replace('.', ',')
}
function inputToCents(s: string): number | null {
  const t = s.trim().replace(',', '.')
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? Math.round(n * 100) : null
}
function litersToInput(s: string | null | undefined): string {
  return s == null ? '' : String(s).replace('.', ',')
}
function inputToLiters(s: string): number | null {
  const t = s.trim().replace(',', '.')
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}
function intToInput(n: number | null | undefined): string {
  return n == null ? '' : String(n)
}
function inputToInt(s: string): number | null {
  const t = s.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? Math.round(n) : null
}
function kmTotal(t: LogbookTrip): number {
  return (t.kmBusiness ?? 0) + (t.kmCommute ?? 0) + (t.kmPrivate ?? 0)
}
function lPer100(t: LogbookTrip): string {
  const km = kmTotal(t)
  const l = t.fuelLiters != null ? Number(t.fuelLiters) : null
  if (!km || l == null) return '—'
  return (l / (km / 100)).toFixed(1).replace('.', ',')
}
// Plausibilität: Summe der km-Kategorien sollte = Ende − Beginn sein.
function kmMismatch(t: LogbookTrip): boolean {
  if (t.startKm == null || t.endKm == null) return false
  return kmTotal(t) !== t.endKm - t.startKm
}

function LogbookPage() {
  const queryClient = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const me = meData?.user
  const canManage =
    me?.role === 'admin' ||
    !!me?.roleObj?.permissions?.all ||
    !!me?.roleObj?.permissions?.manageLogbook

  const { data: vehiclesData } = useQuery({
    queryKey: ['logbook', 'vehicles'],
    queryFn: () => api.listLogbookVehicles(),
    enabled: !!me,
  })
  const vehicles = vehiclesData?.vehicles ?? []

  const [selectedId, setSelectedId] = useState<string | null>(null)
  useEffect(() => {
    if (vehicles.length > 0 && (!selectedId || !vehicles.some((v) => v.id === selectedId))) {
      setSelectedId(vehicles[0].id)
    }
    if (vehicles.length === 0) setSelectedId(null)
  }, [vehicles, selectedId])
  const selected = vehicles.find((v) => v.id === selectedId) ?? null

  const [sharePanel, setSharePanel] = useState<LogbookVehicle | null>(null)
  // Ein Dialog für Anlegen UND Umbenennen — mode entscheidet, id nur beim
  // Umbenennen gesetzt. Ersetzt das dauerhafte Inline-Namensfeld (eine Ebene weg).
  const [vehicleDialog, setVehicleDialog] = useState<{
    mode: 'create' | 'rename'
    id?: string
  } | null>(null)
  const [dialogName, setDialogName] = useState('')

  // Mobile-Shell (<640): Titel oben, die eine Leiste (Chips + Aktionen) bleibt
  // via keep-row sichtbar — Muster Stundenkonten.
  useMobilePageTitle('Fahrtenbücher')

  function invalidateVehicles() {
    queryClient.invalidateQueries({ queryKey: ['logbook', 'vehicles'] })
  }

  const createVehicle = useMutation({
    mutationFn: (name: string) => api.createLogbookVehicle({ name }),
    onSuccess: (res) => {
      invalidateVehicles()
      setSelectedId(res.vehicle.id)
      setVehicleDialog(null)
    },
    onError: (e: Error) => notify.error(e.message),
  })
  const renameVehicle = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.updateLogbookVehicle(id, { name }),
    onSuccess: () => {
      invalidateVehicles()
      setVehicleDialog(null)
    },
    onError: (e: Error) => notify.error(e.message),
  })
  const deleteVehicle = useMutation({
    mutationFn: (id: string) => api.deleteLogbookVehicle(id),
    onSuccess: invalidateVehicles,
    onError: (e: Error) => notify.error(e.message),
  })
  // Neue Fahrt (heutiges Datum) für das gewählte Fahrzeug — die Trip-Liste
  // lebt im Kind (VehicleLogbook) und refetcht über denselben Query-Key.
  const addTrip = useMutation({
    mutationFn: (vehicleId: string) =>
      api.createLogbookTrip(vehicleId, { tripDate: new Date().toISOString().slice(0, 10) }),
    onSuccess: (_res, vehicleId) =>
      queryClient.invalidateQueries({ queryKey: ['logbook', 'trips', vehicleId] }),
    onError: (e: Error) => notify.error(e.message),
  })

  function openCreate() {
    setDialogName('')
    setVehicleDialog({ mode: 'create' })
  }
  function openRename(v: LogbookVehicle) {
    setDialogName(v.name)
    setVehicleDialog({ mode: 'rename', id: v.id })
  }
  function submitVehicleDialog(e?: FormEvent) {
    e?.preventDefault()
    const name = dialogName.trim()
    if (!name || !vehicleDialog) return
    if (vehicleDialog.mode === 'create') {
      if (!createVehicle.isPending) createVehicle.mutate(name)
    } else if (vehicleDialog.id && !renameVehicle.isPending) {
      renameVehicle.mutate({ id: vehicleDialog.id, name })
    }
  }

  if (meLoading) return null
  if (!me) return <Navigate to="/login" />

  return (
    // Kopf/Padding normalisiert (Konzept-Abweichler): kein eigenes p-4/p-6
    // mehr obendrauf auf das Page-Container-Padding aus __root — Wrapper und
    // Kopfzeile folgen jetzt dem Standard-Muster (tickets/projects).
    <div className="space-y-4 animate-fade-in">
      {/* EINE Leiste: Titel + Fahrzeug-Auswahl (inline, mit ＋ Fahrzeug am Ende)
          + Aktionen fürs gewählte Fahrzeug rechts. keep-row = <640 Titel via
          Shell, Chips + Aktionen bleiben als eine scrollbare Leiste sichtbar. */}
      <PageHeader
        title="Fahrtenbücher"
        phoneMode="keep-row"
        inline={
          <TabNav ariaLabel="Fahrzeug wählen">
            {vehicles.map((v) => {
              const active = v.id === selectedId
              return (
                <button
                  key={v.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelectedId(v.id)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 touch:min-h-11 text-sm whitespace-nowrap transition-colors',
                    active
                      ? 'border-accent bg-accent/10 text-accent font-medium'
                      : 'border-border-default text-text-secondary hover:border-accent/40 hover:text-text-primary',
                  )}
                >
                  {v.name}
                  {v.licensePlate ? ` · ${v.licensePlate}` : ''}
                </button>
              )
            })}
            {canManage && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-border-default px-3 py-1.5 touch:min-h-11 text-sm whitespace-nowrap text-text-secondary transition-colors hover:border-accent/40 hover:text-accent"
              >
                <Plus className="h-3.5 w-3.5" />
                Fahrzeug
              </button>
            )}
          </TabNav>
        }
        actions={
          selected && (
            <>
              <Button onClick={() => addTrip.mutate(selected.id)} disabled={!selected._canEdit}>
                <Plus className="h-4 w-4" />
                Neue Fahrt
              </Button>
              {canManage && (
                <>
                  <Button variant="secondary" onClick={() => setSharePanel(selected)}>
                    <Share2 className="h-4 w-4" />
                    Teilen
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Fahrzeug umbenennen"
                    onClick={() => openRename(selected)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="danger"
                    size="icon"
                    aria-label="Fahrzeug löschen"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: 'Fahrzeug löschen?',
                          description: `„${selected.name}“ und ALLE zugehörigen Fahrten werden gelöscht. Das kann nicht rückgängig gemacht werden.`,
                          confirmLabel: 'Löschen',
                          variant: 'danger',
                        })
                      )
                        deleteVehicle.mutate(selected.id)
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </>
          )
        }
      />

      {vehicles.length === 0 ? (
        <EmptyState
          icon={Car}
          message={
            canManage
              ? 'Noch keine Fahrzeuge. Lege mit ‚＋ Fahrzeug‘ dein erstes an.'
              : 'Dir wurde noch kein Fahrtenbuch freigegeben.'
          }
        />
      ) : (
        selected && (
          <VehicleLogbook
            key={selected.id}
            vehicle={selected}
            canEdit={!!selected._canEdit}
            onAddTrip={() => addTrip.mutate(selected.id)}
          />
        )
      )}

      {sharePanel && (
        <EntityAccessPanel
          open={!!sharePanel}
          onClose={() => setSharePanel(null)}
          resourceType="logbook_vehicle"
          resourceId={sharePanel.id}
          resourceName={sharePanel.name}
        />
      )}

      <Dialog
        open={!!vehicleDialog}
        onClose={() => setVehicleDialog(null)}
        title={vehicleDialog?.mode === 'rename' ? 'Fahrzeug umbenennen' : 'Neues Fahrzeug'}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setVehicleDialog(null)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              disabled={!dialogName.trim() || createVehicle.isPending || renameVehicle.isPending}
              onClick={() => submitVehicleDialog()}
            >
              {vehicleDialog?.mode === 'rename' ? 'Speichern' : 'Anlegen'}
            </Button>
          </>
        }
      >
        <form onSubmit={submitVehicleDialog} className="space-y-2 pb-2">
          <Label htmlFor="vehicle-name">Name des Fahrzeugs</Label>
          <Input
            id="vehicle-name"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            placeholder="z.B. Firmen-Sprinter D-AB 1234"
            autoFocus
          />
        </form>
      </Dialog>
    </div>
  )
}

// === Ein Fahrzeug-Fahrtenbuch (Kopf + Tabelle/Formular) ====================
// Transponierte Desktop-Tabelle: Felder als Zeilen (sticky Label-Spalte links),
// eine Fahrt = eine SPALTE, horizontal scrollbar. FIELD_ROWS ist die einzige
// Quelle für Reihenfolge + Zeilenhöhen — Label-Spalte und Trip-Spalten müssen
// zellgenau fluchten.
const inp =
  'w-full bg-transparent px-1 py-0.5 rounded focus:bg-bg-overlay focus:outline-none focus:ring-1 focus:ring-accent/40 disabled:opacity-60'
const ROW_H = 'h-9'
const TIME_ROW_H = 'h-[3.75rem]'
const HEAD_H = 'h-10'

type TripSave = (patch: LogbookTripInput) => void

interface FieldRow {
  key: string
  label: string
  h: string
  render: (trip: LogbookTrip, canEdit: boolean, save: TripSave) => ReactNode
}

function textCell(field: 'route' | 'purpose' | 'contacts' | 'driverName' | 'notes') {
  return (trip: LogbookTrip, canEdit: boolean, save: TripSave) => (
    <input
      defaultValue={trip[field] ?? ''}
      disabled={!canEdit}
      className={inp}
      onBlur={(e) => {
        if ((trip[field] ?? '') !== e.target.value) save({ [field]: e.target.value || null })
      }}
    />
  )
}

function kmCell(field: 'startKm' | 'kmBusiness' | 'kmCommute' | 'kmPrivate' | 'endKm') {
  return (trip: LogbookTrip, canEdit: boolean, save: TripSave) => (
    <input
      inputMode="numeric"
      defaultValue={intToInput(trip[field] as number | null)}
      disabled={!canEdit}
      className={cn(inp, 'text-right tabular-nums')}
      onBlur={(e) => {
        const v = inputToInt(e.target.value)
        const cur = (trip[field] as number | null) ?? null
        // kmBusiness/kmCommute/kmPrivate sind NOT NULL: leer → 0 (wie bisher);
        // startKm/endKm bleiben nullable (matchen den 'km'-Prefix nicht).
        if (v !== cur) save({ [field]: field.startsWith('km') ? (v ?? 0) : v })
      }}
    />
  )
}

const FIELD_ROWS: FieldRow[] = [
  {
    key: 'time',
    label: 'Zeit von–bis',
    h: TIME_ROW_H,
    render: (trip, canEdit, save) => (
      <div className="flex flex-col gap-0.5">
        <input
          type="time"
          defaultValue={trip.startTime ?? ''}
          disabled={!canEdit}
          className={inp}
          onBlur={(e) => {
            if ((trip.startTime ?? '') !== e.target.value)
              save({ startTime: e.target.value || null })
          }}
        />
        <input
          type="time"
          defaultValue={trip.endTime ?? ''}
          disabled={!canEdit}
          className={inp}
          onBlur={(e) => {
            if ((trip.endTime ?? '') !== e.target.value) save({ endTime: e.target.value || null })
          }}
        />
      </div>
    ),
  },
  { key: 'route', label: 'Route & Ziel', h: ROW_H, render: textCell('route') },
  { key: 'purpose', label: 'Zweck', h: ROW_H, render: textCell('purpose') },
  { key: 'contacts', label: 'Personen/Firmen', h: ROW_H, render: textCell('contacts') },
  { key: 'startKm', label: 'km Beginn', h: ROW_H, render: kmCell('startKm') },
  { key: 'kmBusiness', label: 'km geschäftl.', h: ROW_H, render: kmCell('kmBusiness') },
  { key: 'kmCommute', label: 'km Wohn./Arbeit', h: ROW_H, render: kmCell('kmCommute') },
  { key: 'kmPrivate', label: 'km privat', h: ROW_H, render: kmCell('kmPrivate') },
  { key: 'endKm', label: 'km Ende', h: ROW_H, render: kmCell('endKm') },
  {
    key: 'fuelLiters',
    label: 'Kraftst. Ltr.',
    h: ROW_H,
    render: (trip, canEdit, save) => (
      <input
        inputMode="decimal"
        defaultValue={litersToInput(trip.fuelLiters)}
        disabled={!canEdit}
        className={cn(inp, 'text-right tabular-nums')}
        onBlur={(e) => {
          if (litersToInput(trip.fuelLiters) !== e.target.value)
            save({ fuelLiters: inputToLiters(e.target.value) })
        }}
      />
    ),
  },
  {
    key: 'fuelCost',
    label: 'Kraftst. €',
    h: ROW_H,
    render: (trip, canEdit, save) => (
      <input
        inputMode="decimal"
        defaultValue={centsToInput(trip.fuelCostCents)}
        disabled={!canEdit}
        className={cn(inp, 'text-right tabular-nums')}
        onBlur={(e) => {
          if (centsToInput(trip.fuelCostCents) !== e.target.value)
            save({ fuelCostCents: inputToCents(e.target.value) })
        }}
      />
    ),
  },
  {
    key: 'l100',
    label: 'L/100km',
    h: ROW_H,
    render: (trip) => (
      <span className="block w-full px-1 text-right tabular-nums text-text-tertiary">
        {lPer100(trip)}
      </span>
    ),
  },
  {
    key: 'miscCost',
    label: 'Sonstiges €',
    h: ROW_H,
    render: (trip, canEdit, save) => (
      <input
        inputMode="decimal"
        defaultValue={centsToInput(trip.miscCostCents)}
        disabled={!canEdit}
        className={cn(inp, 'text-right tabular-nums')}
        onBlur={(e) => {
          if (centsToInput(trip.miscCostCents) !== e.target.value)
            save({ miscCostCents: inputToCents(e.target.value) })
        }}
      />
    ),
  },
  { key: 'driver', label: 'Fahrer', h: ROW_H, render: textCell('driverName') },
  { key: 'notes', label: 'Notizen', h: ROW_H, render: textCell('notes') },
]

// Reine Tabellen-/Karten-Fläche für das gewählte Fahrzeug — Kopf/Aktionen
// leben jetzt in der einen Leiste der Elternseite (LogbookPage).
function VehicleLogbook({
  vehicle,
  canEdit,
  onAddTrip,
}: {
  vehicle: LogbookVehicle
  canEdit: boolean
  onAddTrip: () => void
}) {
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editTrip, setEditTrip] = useState<LogbookTrip | null>(null)

  const { data: tripsData } = useQuery({
    queryKey: ['logbook', 'trips', vehicle.id],
    queryFn: () => api.listLogbookTrips(vehicle.id),
  })
  const trips = tripsData?.trips ?? []

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['logbook', 'trips', vehicle.id] })
  }

  const saveTrip = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: LogbookTripInput }) =>
      api.updateLogbookTrip(id, patch),
    onSuccess: invalidate,
    onError: (e: Error) => notify.error(e.message),
  })
  const deleteTrip = useMutation({
    mutationFn: (id: string) => api.deleteLogbookTrip(id),
    onSuccess: invalidate,
    onError: (e: Error) => notify.error(e.message),
  })
  const reorderTrips = useMutation({
    mutationFn: (orderedIds: string[]) => api.reorderLogbookTrips(vehicle.id, orderedIds),
    // Antwort trägt die neue Reihenfolge — direkt in den Cache schreiben statt
    // invalidate+Refetch, damit kein Fenster mit alter position entsteht.
    onSuccess: (res) => queryClient.setQueryData(['logbook', 'trips', vehicle.id], res),
    onError: (e: Error) => {
      notify.error(e.message)
      // Optimistische Reihenfolge zurückrollen — Server-Stand neu laden.
      invalidate()
    },
  })

  return (
    <div className="space-y-3">
      {trips.length === 0 ? (
        <EmptyState icon={Car} message="Noch keine Fahrten." hint={'Klicke „Neue Fahrt“.'} />
      ) : (
        <>
          {/* Desktop: transponierte, inline-editierbare Tabelle — eine Fahrt
              pro Spalte, Drag-Handle im Spaltenkopf zum Umsortieren. */}
          <TransposedTripTable
            trips={trips}
            canEdit={canEdit}
            onSave={(id, patch) => saveTrip.mutate({ id, patch })}
            onDelete={(id) => deleteTrip.mutate(id)}
            onAdd={onAddTrip}
            onReorder={(orderedIds) => reorderTrips.mutate(orderedIds)}
          />

          {/* Mobile/Tablet: Karten + Formular-Panel */}
          <div className="md:hidden space-y-2">
            {trips.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setEditTrip(t)
                  setFormOpen(true)
                }}
                className="w-full rounded-lg border border-border-subtle bg-bg-elevated/40 p-3 text-left"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-text-primary">{t.tripDate}</span>
                  <span className="text-xs text-text-tertiary">
                    {t.startTime || '—'}–{t.endTime || '—'} · {kmTotal(t)} km
                  </span>
                </div>
                <div className="text-sm text-text-secondary truncate">{t.route || '—'}</div>
                {t.purpose && (
                  <div className="text-xs text-text-tertiary truncate">{t.purpose}</div>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Panel öffnet ausschließlich zum Bearbeiten (Karte antippen); Anlegen
          läuft über „Neue Fahrt" in der Leiste (heutiges Datum, dann Karte). */}
      <SidePanel
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setEditTrip(null)
        }}
        title="Fahrt bearbeiten"
      >
        {editTrip && (
          <TripForm
            trip={editTrip}
            disabled={!canEdit}
            onSubmit={(data) => {
              saveTrip.mutate({ id: editTrip.id, patch: data })
              setFormOpen(false)
              setEditTrip(null)
            }}
            onDelete={() => {
              deleteTrip.mutate(editTrip.id)
              setFormOpen(false)
              setEditTrip(null)
            }}
          />
        )}
      </SidePanel>
    </div>
  )
}

// Transponierte Desktop-Tabelle: sticky Feldnamen-Spalte + eine Spalte pro
// Fahrt. Drag&Drop-Reorder via dnd-kit (Muster invoices-detail: PointerSensor
// deckt Maus UND Touch ab, touchAction none NUR auf dem Handle).
function TransposedTripTable({
  trips,
  canEdit,
  onSave,
  onDelete,
  onAdd,
  onReorder,
}: {
  trips: LogbookTrip[]
  canEdit: boolean
  onSave: (id: string, patch: LogbookTripInput) => void
  onDelete: (id: string) => void
  onAdd: () => void
  onReorder: (orderedIds: string[]) => void
}) {
  // Optimistische Reihenfolge: Drag sortiert sofort lokal; der Server bestätigt
  // per setQueryData (Reorder-Antwort trägt die neue Reihenfolge), sodass trips
  // in einem Schritt auf den committeten Stand springt — kein Refetch-Fenster,
  // in dem ein paralleler Blur-Save die alte position zurückschriebe.
  const [ordered, setOrdered] = useState(trips)
  useEffect(() => setOrdered(trips), [trips])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = ordered.findIndex((t) => t.id === active.id)
    const newIndex = ordered.findIndex((t) => t.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const next = arrayMove(ordered, oldIndex, newIndex)
    setOrdered(next)
    onReorder(next.map((t) => t.id))
  }

  return (
    <div className="hidden md:block overflow-x-auto rounded-xl border border-border-subtle">
      <div className="flex w-max min-w-full text-xs">
        {/* Sticky Feldnamen-Spalte (Muster team-planning: backdrop-blur) */}
        <div className="sticky left-0 z-20 w-36 shrink-0 border-r border-border-subtle/50 bg-bg-elevated/90 backdrop-blur">
          <div
            className={cn(
              HEAD_H,
              'flex items-center px-2 font-medium text-text-tertiary border-b border-border-subtle',
            )}
          >
            Datum
          </div>
          {FIELD_ROWS.map((r, i) => (
            <div
              key={r.key}
              className={cn(
                r.h,
                'flex items-center px-2 font-medium text-text-tertiary whitespace-nowrap',
                // border-t erst ab Zeile 2 — der Kopf trägt schon border-b.
                i > 0 && 'border-t border-border-subtle/40',
              )}
            >
              {r.label}
            </div>
          ))}
        </div>

        {/* Ghost-Spalte: Neue Fahrt (links, weil neueste vorn einsortiert) */}
        {canEdit && (
          <button
            type="button"
            onClick={onAdd}
            className="flex w-20 shrink-0 flex-col items-center justify-center gap-1 border-r border-dashed border-border-default text-text-tertiary transition-colors hover:bg-bg-overlay/30 hover:text-accent"
          >
            <Plus className="h-4 w-4" />
            <span>Neue Fahrt</span>
          </button>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={ordered.map((t) => t.id)}
            strategy={horizontalListSortingStrategy}
          >
            {ordered.map((t) => (
              <TripColumn
                key={t.id}
                trip={t}
                canEdit={canEdit}
                onSave={(patch) => onSave(t.id, patch)}
                onDelete={() => onDelete(t.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}

// Eine Fahrt als Spalte: Kopf (Handle + Datum + Löschen) + Feldzellen in
// FIELD_ROWS-Reihenfolge. Uncontrolled inputs, Save on blur — beim Drag
// verschiebt dnd-kit den DOM-Knoten samt ungespeicherter Eingaben.
function TripColumn({
  trip,
  canEdit,
  onSave,
  onDelete,
}: {
  trip: LogbookTrip
  canEdit: boolean
  onSave: TripSave
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: trip.id,
  })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 30 : undefined,
      }}
      className={cn(
        'w-44 shrink-0 border-r border-border-subtle/40 bg-bg-base',
        kmMismatch(trip) && 'bg-warning/[0.06]',
        isDragging && 'opacity-90 shadow-lg',
      )}
    >
      <div
        className={cn(
          HEAD_H,
          'flex items-center gap-0.5 border-b border-border-subtle bg-bg-elevated/60 px-1',
        )}
      >
        {canEdit && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label="Fahrt verschieben"
            className="-ml-0.5 shrink-0 cursor-grab touch-none rounded p-1 text-text-tertiary hover:text-accent active:cursor-grabbing touch:opacity-100"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        <input
          type="date"
          defaultValue={trip.tripDate}
          disabled={!canEdit}
          className={cn(inp, 'min-w-0 font-medium')}
          onBlur={(e) => {
            if (trip.tripDate !== e.target.value && e.target.value)
              onSave({ tripDate: e.target.value })
          }}
        />
        {canEdit && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Fahrt löschen"
            className="shrink-0 rounded p-1 text-danger hover:bg-danger/20 touch:opacity-100"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {FIELD_ROWS.map((r, i) => (
        <div
          key={r.key}
          className={cn(
            r.h,
            'flex items-center px-1',
            // border-t erst ab Zeile 2 — der Spaltenkopf trägt schon border-b.
            i > 0 && 'border-t border-border-subtle/40',
          )}
        >
          <div className="w-full">{r.render(trip, canEdit, onSave)}</div>
        </div>
      ))}
    </div>
  )
}

// Formular (Mobile/Tablet) — alle Felder.
// Bearbeitungs-Formular (Mobile/Tablet-Panel) für eine bestehende Fahrt.
function TripForm({
  trip,
  disabled,
  onSubmit,
  onDelete,
}: {
  trip: LogbookTrip
  disabled: boolean
  onSubmit: (data: LogbookTripInput) => void
  onDelete: () => void
}) {
  const [f, setF] = useState({
    tripDate: trip.tripDate,
    startTime: trip.startTime ?? '',
    endTime: trip.endTime ?? '',
    route: trip.route ?? '',
    purpose: trip.purpose ?? '',
    contacts: trip.contacts ?? '',
    startKm: intToInput(trip.startKm),
    kmBusiness: intToInput(trip.kmBusiness),
    kmCommute: intToInput(trip.kmCommute),
    kmPrivate: intToInput(trip.kmPrivate),
    endKm: intToInput(trip.endKm),
    fuelLiters: litersToInput(trip.fuelLiters),
    fuelCost: centsToInput(trip.fuelCostCents),
    miscCost: centsToInput(trip.miscCostCents),
    driverName: trip.driverName ?? '',
    notes: trip.notes ?? '',
  })
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }))

  function submit(e: FormEvent) {
    e.preventDefault()
    onSubmit({
      tripDate: f.tripDate,
      startTime: f.startTime || null,
      endTime: f.endTime || null,
      route: f.route || null,
      purpose: f.purpose || null,
      contacts: f.contacts || null,
      startKm: inputToInt(f.startKm),
      kmBusiness: inputToInt(f.kmBusiness) ?? 0,
      kmCommute: inputToInt(f.kmCommute) ?? 0,
      kmPrivate: inputToInt(f.kmPrivate) ?? 0,
      endKm: inputToInt(f.endKm),
      fuelLiters: inputToLiters(f.fuelLiters),
      fuelCostCents: inputToCents(f.fuelCost),
      miscCostCents: inputToCents(f.miscCost),
      driverName: f.driverName || null,
      notes: f.notes || null,
    })
  }

  const lbl = 'block text-xs text-text-tertiary mb-1'
  return (
    <form onSubmit={submit} className="space-y-3 p-1">
      <div>
        <span className={lbl}>Datum</span>
        <Input
          type="date"
          value={f.tripDate}
          onChange={(e) => set('tripDate', e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className={lbl}>Zeit von</span>
          <Input
            type="time"
            value={f.startTime}
            onChange={(e) => set('startTime', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div>
          <span className={lbl}>Zeit bis</span>
          <Input
            type="time"
            value={f.endTime}
            onChange={(e) => set('endTime', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
      <div>
        <span className={lbl}>Reiseroute und Ziel</span>
        <Input value={f.route} onChange={(e) => set('route', e.target.value)} disabled={disabled} />
      </div>
      <div>
        <span className={lbl}>Zweck der Fahrt</span>
        <Input
          value={f.purpose}
          onChange={(e) => set('purpose', e.target.value)}
          disabled={disabled}
        />
      </div>
      <div>
        <span className={lbl}>Besuchte Personen, Firmen, Behörden</span>
        <Input
          value={f.contacts}
          onChange={(e) => set('contacts', e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className={lbl}>km-Stand Beginn</span>
          <Input
            inputMode="numeric"
            value={f.startKm}
            onChange={(e) => set('startKm', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div>
          <span className={lbl}>km-Stand Ende</span>
          <Input
            inputMode="numeric"
            value={f.endKm}
            onChange={(e) => set('endKm', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div>
          <span className={lbl}>km geschäftlich</span>
          <Input
            inputMode="numeric"
            value={f.kmBusiness}
            onChange={(e) => set('kmBusiness', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div>
          <span className={lbl}>km Wohn./Arbeit</span>
          <Input
            inputMode="numeric"
            value={f.kmCommute}
            onChange={(e) => set('kmCommute', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div>
          <span className={lbl}>km privat</span>
          <Input
            inputMode="numeric"
            value={f.kmPrivate}
            onChange={(e) => set('kmPrivate', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className={lbl}>Kraftstoff Ltr.</span>
          <Input
            inputMode="decimal"
            value={f.fuelLiters}
            onChange={(e) => set('fuelLiters', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div>
          <span className={lbl}>Kraftstoff Betrag €</span>
          <Input
            inputMode="decimal"
            value={f.fuelCost}
            onChange={(e) => set('fuelCost', e.target.value)}
            disabled={disabled}
          />
        </div>
        <div>
          <span className={lbl}>Sonstiges Betrag €</span>
          <Input
            inputMode="decimal"
            value={f.miscCost}
            onChange={(e) => set('miscCost', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
      <div>
        <span className={lbl}>Name des Fahrers</span>
        <Input
          value={f.driverName}
          onChange={(e) => set('driverName', e.target.value)}
          disabled={disabled}
        />
      </div>
      <div>
        <span className={lbl}>Notizen</span>
        <Input value={f.notes} onChange={(e) => set('notes', e.target.value)} disabled={disabled} />
      </div>
      {!disabled && (
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button type="button" variant="danger" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
            Löschen
          </Button>
          <Button type="submit">
            <Pencil className="h-4 w-4" />
            Speichern
          </Button>
        </div>
      )}
    </form>
  )
}
